import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, type UserSearchResult } from "../../api/client";
import UserPicker from "../../components/UserPicker";

const CLAIMANT_SWA_URL =
  (import.meta.env.VITE_CLAIMANT_SWA_URL as string | undefined) ??
  "https://expenses.liaisegroup.com";

interface Section {
  to: string;
  title: string;
  desc: string;
  roles: string[];
  live: boolean;
  external?: boolean;
  badgeKey?: "pending_approvals";
}

const SECTIONS: Section[] = [
  // Cross-app
  { to: CLAIMANT_SWA_URL, title: "My expenses", desc: "Submit your own claims, attach receipts, track approvals.", roles: ["Approver", "Payroll", "Finance", "HomeManager", "Admin", "Claimant.All"], live: true, external: true },

  // Operational queues
  { to: "/approvals", title: "Queues", desc: "Manager queue, payroll queue, awaiting payment, and your history — all in one place.", roles: ["Approver", "Payroll", "Admin"], live: true },
  { to: "/finance", title: "Finance", desc: "Payroll CSV export and cross-home spend reports. Petty cash workflow coming next.", roles: ["Finance", "Admin"], live: true },
  { to: "/admin/all-claims", title: "All claims", desc: "Cross-org oversight. Every claim by every person. Filter by status, date, claimant.", roles: ["Admin"], live: true },
  { to: "/home", title: "My home", desc: "Petty cash float status, log spend, set activity budgets per resident.", roles: ["HomeManager", "Admin"], live: false },

  // Configuration
  { to: "/admin/categories", title: "Categories", desc: "Add, edit, deactivate the expense categories shown to claimants.", roles: ["Admin"], live: true },
  { to: "/admin/mileage-rates", title: "Mileage rates", desc: "Per-vehicle pence-per-mile + tier brackets. Liaise current: 45p flat.", roles: ["Admin"], live: true },
  { to: "/admin/cost-centres", title: "Cost centres", desc: "Homes, Support Office sub-units, sub-brand assignment.", roles: ["Admin"], live: true },
  { to: "/admin/policy", title: "Policy", desc: "Receipt threshold, payroll-skip threshold, closed-period rule.", roles: ["Admin"], live: true },
  { to: "/admin/manager-overrides", title: "Manager overrides", desc: "Pin a specific approver for a claimant when Entra is wrong.", roles: ["Admin"], live: true },
  { to: "/admin/roles", title: "Roles", desc: "Add or remove staff from Approvals · Payroll · Finance · Home Manager · Admin groups.", roles: ["Admin"], live: true },
  { to: "/admin/notifications", title: "Notifications", desc: "Switch automated expense emails on or off org-wide (claimant paid, payroll ready, recall, etc.).", roles: ["Admin"], live: true },
  { to: "/admin/email-previews", title: "Email previews", desc: "Send any of the live notification emails to your inbox to review styling and copy.", roles: ["Admin"], live: true },
  { to: "/admin/email-prefs", title: "My email preferences", desc: "Choose which expense notifications you want emailed to you.", roles: ["Approver", "Payroll", "Finance", "Admin"], live: true },
];

export default function AdminLanding() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const roles = me?.roles ?? [];
  const visible = SECTIONS.filter((s) => s.roles.some((r) => roles.includes(r)));
  const canSearchClaimants = roles.some((r) => ["Admin", "Payroll", "Finance", "Approver"].includes(r));
  const navigate = useNavigate();
  const [picked, setPicked] = useState<UserSearchResult | null>(null);

  function jumpToClaimant(u: UserSearchResult | null) {
    if (u) navigate(`/admin/claimant/${u.oid}`);
    setPicked(null);
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Admin</h1>
        <p className="text-slate-600 mt-1">All admin tools in one place — operational queues + configuration.</p>
      </div>

      {canSearchClaimants && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-medium text-liaise-navy">Find a claimant</h2>
            <span className="text-xs text-slate-500">Search Entra by name or email — opens their full claim history.</span>
          </div>
          <UserPicker
            selected={picked}
            onChange={jumpToClaimant}
            placeholder="Type a name or email…"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map((s) => {
          const cardClass = `bg-white border border-slate-200 rounded-xl p-6 shadow-sm transition ${
            s.live ? "hover:shadow-md hover:border-liaise-navy/30" : "opacity-60 cursor-not-allowed"
          }`;
          const inner = (
            <>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="font-medium text-liaise-navy">{s.title}</h2>
                {!s.live && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Coming soon</span>}
                {s.external && <span className="text-xs bg-liaise-navy/10 text-liaise-navy px-2 py-0.5 rounded">↗ Opens claimant app</span>}
              </div>
              <p className="text-slate-600 text-sm">{s.desc}</p>
            </>
          );
          if (s.external) {
            return (
              <a key={s.to} href={s.to} target="_blank" rel="noopener noreferrer" className={cardClass}>
                {inner}
              </a>
            );
          }
          return (
            <Link
              key={s.to}
              to={s.live ? s.to : "#"}
              onClick={(e) => !s.live && e.preventDefault()}
              className={cardClass}
            >
              {inner}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
